import clsx from 'clsx'
import { forwardRef } from 'react'
import { ArrowUp, StopCircle } from '@/assets/icons'

type ChatInputButtonProps = {
  canSubmit: boolean
  isRunning: boolean
  isStopping: boolean
  onStop: () => void
}

/** Sends the composer's message, or stops the running response. */
const ChatInputButton = forwardRef<HTMLButtonElement, ChatInputButtonProps>(function ChatInputButton({ canSubmit, isRunning, isStopping, onStop }, ref) {
  // Once Stop is requested, the button rests in its disabled send look until the run ends.
  const showStop = isRunning && !isStopping

  // One button element for both states, so keyboard focus stays on it when send becomes stop.
  return (
    <button
      ref={ref}
      aria-label={isRunning ? (isStopping ? 'Stopping response' : 'Stop response') : 'Send message'}
      className={clsx(
        'agent-v2-input-submit relative inline-flex h-10 w-10 items-center justify-center rounded-md text-white transition-colors duration-200',
        'focus:outline-hidden focus-visible:outline focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed',
        showStop
          ? 'bg-brand-neutral-950 focus-visible:outline-brand-neutral-900 enabled:hover:bg-red-800 enabled:active:bg-red-800'
          : 'bg-brand-500 focus-visible:outline-brand-500 hover:bg-brand-800 active:bg-brand-800 disabled:bg-true-gray-200'
      )}
      disabled={isRunning ? isStopping : !canSubmit}
      onClick={isRunning ? onStop : undefined}
      type={isRunning ? 'button' : 'submit'}
    >
      {showStop ? (
        <>
          <StopCircle aria-hidden className="h-6 w-6" />
          <svg
            aria-hidden
            className="agent-v2-input-submit-ring absolute h-[30px] w-[30px] motion-safe:animate-[spin_0.95s_cubic-bezier(0.45,0.15,0.55,0.85)_infinite]"
            fill="none"
            viewBox="0 0 30 30"
          >
            <circle cx="15" cy="15" r="14" stroke="currentColor" strokeOpacity="0.35" strokeWidth="2" />
            <circle cx="15" cy="15" r="14" stroke="currentColor" strokeDasharray="29 59" strokeLinecap="round" strokeWidth="2" />
          </svg>
        </>
      ) : (
        <ArrowUp aria-hidden className="h-6 w-6" />
      )}
    </button>
  )
})

export default ChatInputButton
